import { ReactElement } from 'react';
import Sections from '~/components/layout/Sections';
import PerformableGroupForm, {
  PerformableGroupNested,
  usePerformableGroup,
} from '~/components/performable/PerformableGroupForm';
import ZodFieldHidden from '~/components/zod/ZodFieldHidden';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import BrePackageGroup from '~/tenants/bre/package/BrePackageGroup';
import {
  BrePackageBoostInclude,
  BrePackageFloorplanInclude,
  BrePackageMatterportUpgrade,
  BrePackagePhotosInclude,
  BrePackageWebsiteInclude,
} from '~/tenants/bre/package/common';
import BreBoostConfig from '~/tenants/bre/performable/boost/BreBoostConfig';
import { BreBoostFields } from '~/tenants/bre/performable/boost/BreBoostFields';
import BreFloorplanConfig from '~/tenants/bre/performable/floorplan/BreFloorplanConfig';
import BreMatterportAddOns from '~/tenants/bre/performable/matterport/BreMatterportAddOns';
import BreMatterportConfig from '~/tenants/bre/performable/matterport/BreMatterportConfig';
import BrePhotosCommunityAddOn from '~/tenants/bre/performable/photos/BrePhotosCommunityAddOn';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import BrePhotosFields from '~/tenants/bre/performable/photos/BrePhotosFields';
import BrePhotosLocalAreaAddOn from '~/tenants/bre/performable/photos/BrePhotosLocalAreaAddOn';
import BreWebsiteConfig from '~/tenants/bre/performable/website/BreWebsiteConfig';
import BreWebsiteDomianAddOn from '~/tenants/bre/performable/website/BreWebsiteDomianAddOn';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { TenantPackageIncludes } from '~/tenants/common/package';
import BreFloorplanAddOns from '~/tenants/bre/performable/floorplan/BreFloorplanAddOns';

export default function BrePhotoPackageForm(): ReactElement {
  const { hasPerformable, order } = usePerformableGroup<BreOrderContext>();

  return (
    <Sections>
      <TenantPackageIncludes>
        <PerformableGroupNested performable={BrePhotosConfig}>
          <BrePackagePhotosInclude itemNum={1} />
        </PerformableGroupNested>
        <BrePackageFloorplanInclude itemNum={2} />
        <BrePackageWebsiteInclude itemNum={3} />
        <PerformableGroupNested performable={BreBoostConfig}>
          <BrePackageBoostInclude itemNum={4} />
        </PerformableGroupNested>
      </TenantPackageIncludes>

      <BrePackageGroup itemNum={1} subtitle="Customize the ground photography." performable={BrePhotosConfig}>
        <BrePhotosFields context={order} />
        <BrePhotosCommunityAddOn context={order} persona={PersonaType.CUSTOMER} />
        <BrePhotosLocalAreaAddOn context={order} persona={PersonaType.CUSTOMER} />
      </BrePackageGroup>

      <BrePackageGroup itemNum={2} subtitle="Earth-Tone Color 2D Floorplan." performable={BreFloorplanConfig}>
        <BrePackageMatterportUpgrade />
        <BreFloorplanAddOns context={order} persona={PersonaType.CUSTOMER} />
      </BrePackageGroup>

      {hasPerformable(BreMatterportConfig) && (
        <PerformableGroupForm performable={BreMatterportConfig}>
          <BreMatterportAddOns context={order} persona={PersonaType.CUSTOMER} />
        </PerformableGroupForm>
      )}

      <BrePackageGroup
        itemNum={3}
        subtitle="Market your listing in the city of the property."
        performable={BreBoostConfig}
      >
        <BreBoostFields />
      </BrePackageGroup>

      <BrePackageGroup
        itemNum={4}
        subtitle="1-year subscription to a single property website page"
        performable={BreWebsiteConfig}
      >
        <ZodFieldHidden name="type" value="website" />
        <BreWebsiteDomianAddOn type={order.metadata.type} persona={PersonaType.CUSTOMER} />
      </BrePackageGroup>
    </Sections>
  );
}
